import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import { useStore } from '../../../../../../stores/root';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../../services/general/error';

// props

// components
import { CommandBar, IColumn, ICommandBarItemProps, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Text from './../../../../../typography/text';
import { IPaginationResourceShort } from '../../../../../../props/general';
import Pagination from '../../../../../uiframeworks/pagination';
import { NavLink } from 'react-router-dom';
import PermissionsService from '../../../../../../services/permissions';
import { IDepositResourceShortProps } from '../../../../../../props/finance/incomes/deposits';
import DepositService from '../../../../../../services/finance/deposits';
import GeneralService from '../../../../../../services/general';
import { IDepositTransactionResourceShortProps } from '../../../../../../props/finance/incomes/deposits/transactions';
import moment from 'moment';
import DepositTransactionService from '../../../../../../services/finance/deposits/transaction';
import SelectOutcomeInvoice from '../../../../../uiframeworks/forms/finances/outcomes/invoice';
import SelectOutcomePaymentCompletion from '../../../../../uiframeworks/forms/finances/outcomes/paymentCompletion';

interface DepositTransactionsListProps {
    qs?: string[];
    transactions?: IDepositTransactionResourceShortProps[];
    columns?: TDepositsListColumn[];
    hideSearch?: boolean;
    hideCommandBar?: boolean;
    hideCreateButton?: boolean;
    variant?: 'card' | 'plain';
}

export type TDepositsListColumn = 'user' | 'date' | 'amount' | 'reason' | 'createdBy' | 'invoice';

const DepositTransactionsList: React.FC<DepositTransactionsListProps> = (props: DepositTransactionsListProps) => {
    const shownColumns: TDepositsListColumn[] = props.columns || [
        'user',
        'invoice',
        'date',
        'reason',
        'createdBy',
        'amount'
    ];

    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [transactions, setTransactions] = React.useState<IDepositTransactionResourceShortProps[]>(props.transactions || []);
    const [pagination, setPagination] = React.useState<IPaginationResourceShort | undefined>();
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const columns: IColumn[] = [
        {
            key: "user",
            name: "Customer / Employee / Vendor",
            minWidth: 300,
            maxWidth: 300,
            isMultiline: true,
            onRender: (item: IDepositTransactionResourceShortProps) => {
                let url: string = "";
                if (item.employee) {
                    url = `/users/customers/${item.employee.id}`; 
                } else if (item.vendor) {
                    url = `/operationals/vendors/${item.vendor.id}`;
                } else if (item.company) {
                    url = `/users/companies/${item.company.id}`;
                }

                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <NavLink to={url}>
                        {item.employee ? <Text size={'small'}>{item.employee.name}</Text> : null}
                        {item.company ? <Text size={'small'}>{item.company.name}</Text> : null}
                        {item.vendor ? <Text size={'small'}>{item.vendor.name}</Text> : null}
                    </NavLink>
                </Stack>
            }
        },
        {
            key: "invoice",
            name: "Related Invoice / Payment",
            minWidth: 300,
            maxWidth: 300,
            onRender: (item: IDepositTransactionResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    {item.outcomeInvoice ? <SelectOutcomeInvoice hideLabel
                        onChange={() => {}}
                        disabled
                        openDetailsOnTitleClick
                        selected={item.outcomeInvoice} /> : null}
                    {/*item.outcomePaymentCompletion ? <SelectOutcomePaymentCompletion hideLabel
                        onChange={() => {}}
                        disabled
                        openDetailsOnTitleClick
                        selected={item.outcomePaymentCompletion} /> : null*/}
                </Stack>
            }
        },
        {
            key: "reason",
            name: "Reason",
            minWidth: 225,
            isMultiline: true,
            onRender: (item: IDepositTransactionResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>{item.reason || ""}</Text>
                </Stack>
            }
        },
        {
            key: "date",
            name: "Executed At / By",
            minWidth: 225,
            maxWidth: 225,
            isMultiline: true,
            onRender: (item: IDepositTransactionResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>{moment(item.createdAt).format("DD/MM/YYYY")} by {item.createdBy.name}</Text>
                </Stack>
            }
        },
        {
            key: "amount",
            name: "Amount",
            minWidth: 200,
            maxWidth: 200,
            onRender: (item: IDepositResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    {Number(item.amount) >= 0 ? <Text size={'small'} className={'color-green'}>Rp. {GeneralService.getNumberWithSeparator(Number(item.amount))}</Text> : null}
                    {Number(item.amount) < 0 ? <Text size={'small'} className={'color-red'}>- Rp. {GeneralService.getNumberWithSeparator(Number(item.amount) * -1)}</Text> : null}
                </Stack>
            }
        }
    ];

    useEffect(() => {
        if (!props.transactions) {
            _onRetrieveTransactions();
        } else {
            setLoaded(true);
        }
    }, [keyword]);

    const _onRetrieveTransactions = async (pageNumber?: number) => {
        try {
            setLoaded(false);

            const qs: string[] = props.qs || [];
            if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }

            const _results = await DepositTransactionService.retrieve(qs.join("&"));
            setTransactions(_results.data);
            setPagination(_results.pagination);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_deposit_transactions_list_error',
                text: 'Failed to retrieve deposit transactions list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        if (!props.hideSearch) {
            items.push({
                key: "search",
                onRender: () => {
                    return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} />
                }
            });
        }

        return { items, farItems };
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(false);
        setKeyword(value || "");
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        {!props.hideCommandBar ? <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{
                root: {
                    padding: 0,
                    height: 'unset',
                    backgroundColor: 'transparent'
                }
            }}
            ariaLabel="Use left and right arrow keys to navigate between commands" /> : null}
        <Stack tokens={{ childrenGap: 10 }}>
            <Stack className={styles[props.variant || 'card']}>
                {
                    !loaded || (loaded && transactions.length > 0) ? (
                        <>
                            <ShimmeredDetailsList
                                setKey="items"
                                items={transactions}
                                columns={columns.filter((col) => shownColumns.indexOf(col.key as TDepositsListColumn) > -1)}
                                selectionMode={SelectionMode.none}
                                enableShimmer={!loaded}
                                onShouldVirtualize={() => false}
                                ariaLabelForShimmer="Content is being fetched"
                                ariaLabelForGrid="Item details" />
                        </>
                    ) : null
                }
                {loaded && transactions.length < 1 ? <Stack styles={{ root: { padding: props.variant === 'plain' ? 0 : 10 } }}>
                    <Text>No transactions found in any vendors/users</Text>
                </Stack> : null}
            </Stack>
            {pagination && transactions.length > 0 ? <Pagination goToPage={_onRetrieveTransactions} currentPage={pagination.currentPage} totalPage={pagination.lastPage} /> : null}
        </Stack>
    </Stack>;
};

export default DepositTransactionsList;
